<template>
  <div class="chat-configure-form mt-4">
    <div
      class="Polaris-InlineGrid"
      style="--pc-inline-grid-grid-template-columns-xs:minmax(0, 1fr);--pc-inline-grid-grid-template-columns-md:minmax(0, 2fr) minmax(0, 1fr);--pc-inline-grid-gap-xs:var(--p-space-400)"
    >
      <!-- Left column: Configuration options -->
      <div style="display:inherit;width:auto;border-inline-start:none">
        <div
          class="Polaris-InlineStack"
          style="--pc-inline-stack-align:center;--pc-inline-stack-wrap:wrap;--pc-inline-stack-gap-xs:var(--p-space-400);--pc-inline-stack-flex-direction-xs:row"
        >
          <div style="color:var(--p-color-text-info-on-bg-fill);width:100%">
            <div class="Polaris-LegacyCard">
              <!-- Office Hours Header Section -->
              <div class="Polaris-LegacyCard__Section">
                <div
                  class="Polaris-LegacyStack Polaris-LegacyStack--alignmentCenter"
                >
                  <div
                    class="Polaris-LegacyStack__Item Polaris-LegacyStack__Item--fill"
                  >
                    <h2 class="Polaris-Text--root Polaris-Text--headingMd">
                      Office Hours
                    </h2>
                    <p class="mt-1 text-sm leading-5 text-gray-500">
                      Set office hours for your agents. If the customer visits
                      your website outside of your office hours we wont show the
                      contact button.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Enable Office Hours Section -->
              <div class="Polaris-LegacyCard__Section border-b border-gray-200">
                <Alert
                  v-if="config.officeHours.isEnabled"
                  :type="'warning'"
                  :text="alertText"
                />
                <div
                  class="Polaris-LegacyStack Polaris-LegacyStack--alignmentCenter"
                >
                  <div
                    class="Polaris-LegacyStack__Item Polaris-LegacyStack__Item--fill"
                  >
                    <h2 class="Polaris-Text--root Polaris-Text--headingMd">
                      Enable office hours
                    </h2>
                  </div>
                </div>

                <div class="mt-4 flex flex-wrap">
                  <VSwitch
                    v-model="config.officeHours.isEnabled"
                    @input="setDefaultOfficeHours"
                  />
                </div>
              </div>

              <!-- Timezone Settings Section -->
              <div
                v-if="config.officeHours.isEnabled"
                class="Polaris-LegacyCard__Section border-b border-gray-200"
              >
                <div
                  class="Polaris-LegacyStack Polaris-LegacyStack--alignmentCenter"
                >
                  <div
                    class="Polaris-LegacyStack__Item Polaris-LegacyStack__Item--fill"
                  >
                    <h2 class="Polaris-Text--root Polaris-Text--headingMd">
                      Time zone
                    </h2>
                  </div>
                </div>

                <div
                  class="Polaris-BlockStack"
                  style="--pc-block-stack-order:column;--pc-block-stack-gap-xs:var(--p-space-400)"
                >
                  <div
                    class="Polaris-BlockStack"
                    style="--pc-block-stack-order:column;--pc-block-stack-gap-xs:var(--p-space-200)"
                    role="group"
                  >
                    <div class="Polaris-FormLayout__Item mt-4">
                      <div class="Polaris-Connected">
                        <div
                          class="Polaris-Connected__Item Polaris-Connected__Item--primary"
                        >
                          <div class="Polaris-Select">
                            <select
                              v-model="config.officeHours.timezone"
                              class="Polaris-Select__Input"
                              aria-invalid="false"
                            >
                              <option
                                v-for="(option, index) in timezoneOptions"
                                :key="index"
                                :value="option.value"
                              >
                                {{ option.text }}
                              </option>
                            </select>
                            <div
                              class="Polaris-Select__Content"
                              aria-hidden="true"
                            >
                              <span class="Polaris-Select__SelectedOption">
                                {{ getSelectedTimezoneText() }}
                              </span>
                              <span class="Polaris-Select__Icon">
                                <span class="Polaris-Icon">
                                  <svg
                                    viewBox="0 0 20 20"
                                    class="Polaris-Icon__Svg"
                                    focusable="false"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M10.884 4.323a1.25 1.25 0 0 0-1.768 0l-2.646 2.647a.75.75 0 0 0 1.06 1.06l2.47-2.47 2.47 2.47a.75.75 0 1 0 1.06-1.06l-2.646-2.647Z"
                                    ></path>
                                    <path
                                      d="m13.53 13.03-2.646 2.647a1.25 1.25 0 0 1-1.768 0l-2.646-2.647a.75.75 0 0 1 1.06-1.06l2.47 2.47 2.47-2.47a.75.75 0 0 1 1.06 1.06Z"
                                    ></path>
                                  </svg>
                                </span>
                              </span>
                            </div>
                            <div class="Polaris-Select__Backdrop"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hours Settings Section -->
              <div
                v-if="config.officeHours.isEnabled"
                class="Polaris-LegacyCard__Section"
              >
                <div
                  class="Polaris-LegacyStack Polaris-LegacyStack--alignmentCenter"
                >
                  <div
                    class="Polaris-LegacyStack__Item Polaris-LegacyStack__Item--fill"
                  >
                    <h2 class="Polaris-Text--root Polaris-Text--headingMd">
                      Hours
                    </h2>
                  </div>
                </div>

                <div class="Polaris-DataTable mt-4">
                  <div class="Polaris-DataTable__TableContainer">
                    <table class="Polaris-DataTable__Table">
                      <tbody>
                        <tr
                          v-for="(officeHour, index) in officeHours"
                          :key="index"
                          class="Polaris-DataTable__TableRow"
                        >
                          <td
                            class="Polaris-DataTable__Cell Polaris-DataTable__Cell--verticalAlignTop"
                          >
                            <span
                              class="Polaris-Text--root Polaris-Text--bodySm"
                            >
                              {{ dayTitle[index + 1] }}
                            </span>
                          </td>
                          <td class="Polaris-DataTable__Cell">
                            <div class="flex items-center">
                              <!-- Open Time Select -->
                              <div class="Polaris-Select mr-2">
                                <select
                                  v-model="officeHour.officeHoursOpen"
                                  class="Polaris-Select__Input"
                                  :disabled="officeHour.officeClosed"
                                  @input="updateOfficeHours"
                                >
                                  <option
                                    v-for="(option, idx) in timeOptions"
                                    :key="idx"
                                    :value="option.value"
                                  >
                                    {{ option.text }}
                                  </option>
                                </select>
                                <div
                                  class="Polaris-Select__Content"
                                  aria-hidden="true"
                                >
                                  <span class="Polaris-Select__SelectedOption">
                                    {{
                                      getTimeText(officeHour.officeHoursOpen)
                                    }}
                                  </span>
                                  <span class="Polaris-Select__Icon">
                                    <span class="Polaris-Icon">
                                      <svg
                                        viewBox="0 0 20 20"
                                        class="Polaris-Icon__Svg"
                                        focusable="false"
                                        aria-hidden="true"
                                      >
                                        <path
                                          d="M10.884 4.323a1.25 1.25 0 0 0-1.768 0l-2.646 2.647a.75.75 0 0 0 1.06 1.06l2.47-2.47 2.47 2.47a.75.75 0 1 0 1.06-1.06l-2.646-2.647Z"
                                        ></path>
                                        <path
                                          d="m13.53 13.03-2.646 2.647a1.25 1.25 0 0 1-1.768 0l-2.646-2.647a.75.75 0 0 1 1.06-1.06l2.47 2.47 2.47-2.47a.75.75 0 0 1 1.06 1.06Z"
                                        ></path>
                                      </svg>
                                    </span>
                                  </span>
                                </div>
                                <div class="Polaris-Select__Backdrop"></div>
                              </div>

                              <div
                                class="w-6 border border-gray-200 mx-2"
                              ></div>

                              <!-- Close Time Select -->
                              <div class="Polaris-Select mr-4">
                                <select
                                  v-model="officeHour.officeHoursClosed"
                                  class="Polaris-Select__Input"
                                  :disabled="officeHour.officeClosed"
                                  @input="updateOfficeHours"
                                >
                                  <option
                                    v-for="(option, idx) in timeOptions"
                                    :key="idx"
                                    :value="option.value"
                                  >
                                    {{ option.text }}
                                  </option>
                                </select>
                                <div
                                  class="Polaris-Select__Content"
                                  aria-hidden="true"
                                >
                                  <span class="Polaris-Select__SelectedOption">
                                    {{
                                      getTimeText(officeHour.officeHoursClosed)
                                    }}
                                  </span>
                                  <span class="Polaris-Select__Icon">
                                    <span class="Polaris-Icon">
                                      <svg
                                        viewBox="0 0 20 20"
                                        class="Polaris-Icon__Svg"
                                        focusable="false"
                                        aria-hidden="true"
                                      >
                                        <path
                                          d="M10.884 4.323a1.25 1.25 0 0 0-1.768 0l-2.646 2.647a.75.75 0 0 0 1.06 1.06l2.47-2.47 2.47 2.47a.75.75 0 1 0 1.06-1.06l-2.646-2.647Z"
                                        ></path>
                                        <path
                                          d="m13.53 13.03-2.646 2.647a1.25 1.25 0 0 1-1.768 0l-2.646-2.647a.75.75 0 0 1 1.06-1.06l2.47 2.47 2.47-2.47a.75.75 0 0 1 1.06 1.06Z"
                                        ></path>
                                      </svg>
                                    </span>
                                  </span>
                                </div>
                                <div class="Polaris-Select__Backdrop"></div>
                              </div>

                              <!-- Office Closed Checkbox -->
                              <label
                                class="Polaris-Choice Polaris-Checkbox__ChoiceLabel"
                              >
                                <span class="Polaris-Choice__Control">
                                  <span class="Polaris-Checkbox">
                                    <input
                                      type="checkbox"
                                      class="Polaris-Checkbox__Input"
                                      v-model="officeHour.officeClosed"
                                    />
                                    <span
                                      class="Polaris-Checkbox__Backdrop"
                                    ></span>
                                    <span
                                      class="Polaris-Checkbox__Icon Polaris-Checkbox--animated"
                                    >
                                      <svg
                                        viewBox="0 0 16 16"
                                        shape-rendering="geometricPrecision"
                                        text-rendering="geometricPrecision"
                                      >
                                        <path
                                          class=""
                                          d="M1.5,5.5L3.44655,8.22517C3.72862,8.62007,4.30578,8.64717,4.62362,8.28044L10.5,1.5"
                                          transform="translate(2 2.980376)"
                                          opacity="0"
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          pathLength="1"
                                        ></path>
                                      </svg>
                                    </span>
                                  </span>
                                </span>
                                <span class="Polaris-Choice__Label">
                                  <span
                                    class="Polaris-Text--root Polaris-Text--bodyMd"
                                  >
                                    Office Closed
                                  </span>
                                </span>
                              </label>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VSwitch from "../../components/Common/VSwitch";
import Timezone from "../../Mixins/timezones";
import Alert from "../../components/Common/Alert";

export default {
  name: "OfficeHours",
  mixins: [Timezone],
  components: {
    VSwitch,
    Alert
  },
  model: {
    prop: "config",
    event: "change"
  },
  props: {
    config: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  mounted() {
    if (
      this.config.officeHours.officeHours &&
      this.config.officeHours.officeHours.length > 0
    ) {
      this.officeHours = this.config.officeHours.officeHours;
    }
  },
  data() {
    return {
      alertText:
        "If office hours are enabled in the chat. We won't show the button if the time lies away from office hours. If you would like to always show up, please disable office hours.",
      timezoneOptions: this.getTimezoneOptions(),
      dayTitle: {
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
        7: "Sunday"
      },
      officeHours: [
        {
          officeHoursOpen: 9,
          officeHoursClosed: 17,
          officeClosed: false
        },
        {
          officeHoursOpen: 9,
          officeHoursClosed: 17,
          officeClosed: false
        },
        {
          officeHoursOpen: 9,
          officeHoursClosed: 17,
          officeClosed: false
        },
        {
          officeHoursOpen: 9,
          officeHoursClosed: 17,
          officeClosed: false
        },
        {
          officeHoursOpen: 9,
          officeHoursClosed: 17,
          officeClosed: false
        },
        {
          officeHoursOpen: 9,
          officeHoursClosed: 17,
          officeClosed: true
        },
        {
          officeHoursOpen: 9,
          officeHoursClosed: 17,
          officeClosed: true
        }
      ],
      timeOptions: [
        { text: "12 am", value: 0 },
        { text: "1 am", value: 1 },
        { text: "2 am", value: 2 },
        { text: "3 am", value: 3 },
        { text: "4 am", value: 4 },
        { text: "5 am", value: 5 },
        { text: "6 am", value: 6 },
        { text: "7 am", value: 7 },
        { text: "8 am", value: 8 },
        { text: "9 am", value: 9 },
        { text: "10 am", value: 10 },
        { text: "11 am", value: 11 },
        { text: "12 pm", value: 12 },
        { text: "1 pm", value: 13 },
        { text: "2 pm", value: 14 },
        { text: "3 pm", value: 15 },
        { text: "4 pm", value: 16 },
        { text: "5 pm", value: 17 },
        { text: "6 pm", value: 18 },
        { text: "7 pm", value: 19 },
        { text: "8 pm", value: 20 },
        { text: "9 pm", value: 21 },
        { text: "10 pm", value: 22 },
        { text: "11 pm", value: 23 }
      ]
    };
  },
  methods: {
    updateOfficeHours() {
      this.config.officeHours.officeHours = this.officeHours;
      this.$emit("save");
    },
    getSelectedTimezoneText() {
      const timezone = this.timezoneOptions.find(
        option => option.value === this.config.officeHours.timezone
      );
      return timezone ? timezone.text : "";
    },
    getTimeText(value) {
      const time = this.timeOptions.find(option => option.value === value);
      return time ? time.text : "";
    },
    setDefaultOfficeHours() {
      if (
        this.config.officeHours.isEnabled &&
        (!this.config.officeHours.officeHours ||
          this.config.officeHours.officeHours.length == 0)
      ) {
        this.config.officeHours.officeHours = this.officeHours;
        this.config.officeHours.timezone = this.timezoneOptions[0].value;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.chat-configure-form .Polaris-LegacyCard {
  overflow: visible;
}

.chat-configure-form .Polaris-Connected__Item--primary {
  z-index: unset;
  position: unset;
}

.chat-configure-form .Polaris-Connected__Item--primary:focus {
  z-index: 10000;
}
</style>
